/**
 * VelocyTrack Environment & Branding Helper
 * Resolves active environment ('development', 'beta', 'production')
 * and returns environment-tailored brand logos, colors, and favicons.
 */

export function getActualHostEnvironment() {
    // Never allow development simulator mode in compiled production bundles
    if (process.env.NODE_ENV === 'production') {
        const rawProdEnv = (process.env.VUE_APP_ENV_NAME || '').trim().toLowerCase();
        if (rawProdEnv === 'beta' || rawProdEnv === 'staging') return 'beta';
        if (rawProdEnv === 'production' || rawProdEnv === 'prod') return 'production';
        if (typeof window !== 'undefined' && window.location && window.location.hostname) {
            const h = window.location.hostname.toLowerCase();
            if (h.includes('beta.')) return 'beta';
        }
        return 'production';
    }

    // 1. Explicit VUE_APP_ENV_NAME build parameter
    const rawEnv = (process.env.VUE_APP_ENV_NAME || '').trim().toLowerCase();
    if (rawEnv === 'production' || rawEnv === 'prod') return 'production';
    if (rawEnv === 'beta' || rawEnv === 'staging') return 'beta';
    if (rawEnv === 'development' || rawEnv === 'dev') return 'development';

    // 2. Runtime hostname inspection
    if (typeof window !== 'undefined' && window.location && window.location.hostname) {
        const host = window.location.hostname.toLowerCase();
        if (host === 'velocytrack.com' || host === 'www.velocytrack.com') {
            return 'production';
        }
        if (host === 'beta.velocytrack.com' || host === 'beta.velocytrack.dev' || host === 'beta.velocytrack.local' || host.includes('beta.')) {
            return 'beta';
        }
        if (host === 'localhost' || host === '127.0.0.1' || host === 'velocytrack.dev' || host.endsWith('.dev') || host === 'velocytrack.local' || host.endsWith('.local') || host.includes('local.')) {
            return 'development';
        }
    }

    // 3. Fallback based on NODE_ENV
    if (process.env.NODE_ENV === 'production') {
        return 'production';
    }
    return 'development';
}

export function getAppEnvironment(overrideEnv = null) {
    const actual = getActualHostEnvironment();
    if (actual !== 'development') {
        return actual;
    }
    const candidate = (overrideEnv || (typeof localStorage !== 'undefined' ? localStorage.getItem('velocytrack_dev_sim_env') : '') || '').trim().toLowerCase();
    if (candidate === 'beta' || candidate === 'production' || candidate === 'development') {
        return candidate;
    }
    return actual;
}

export function getLogoUrl(env = getAppEnvironment()) {
    const base = process.env.BASE_URL || '/';
    if (env === 'production') {
        return `${base}logo-prod.png`;
    }
    if (env === 'development') {
        return `${base}logo-dev.png`;
    }
    return `${base}logo-beta.png`;
}

export function getLogoColor(env = getAppEnvironment()) {
    if (env === 'production') {
        return '#d11cf4';
    }
    if (env === 'development') {
        return '#08eb26';
    }
    return '#fbbf24';
}

export function setEnvironmentFavicons(env = getAppEnvironment()) {
    if (typeof document === 'undefined') return;
    const base = process.env.BASE_URL || '/';
    const suffix = env === 'production' ? '-prod' : (env === 'development' ? '-dev' : '-beta');

    const updateLink = (selector, href) => {
        const link = document.querySelector(selector);
        if (link) {
            link.href = href;
        }
    };

    updateLink('link[rel="icon"][sizes="32x32"]', `${base}favicon${suffix}-32x32.png`);
    updateLink('link[rel="icon"][sizes="16x16"]', `${base}favicon${suffix}-16x16.png`);
    updateLink('link[rel="apple-touch-icon"]', `${base}apple-touch-icon${suffix}.png`);
    updateLink('link[rel="alternate icon"]', `${base}favicon${suffix}.ico`);
}
